import { useMemo } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { useFormContext, useWatch } from "react-hook-form";

import { Option, Section } from "src/base-components/Select";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import {
  CasePropertiesValue,
  Config1,
  LinkedBlockConfigAttribute,
  LinkedBlockConfigAttributeKind,
} from "src/clients/case-management";
import { TYPE_ICONS } from "src/utils/constants";

export const DECISION_FLOW_MAPPING_KEY = "#additional_inputs";

const SECTIONS = {
  properties: { key: "properties", displayValue: "Properties" },
  decisionFlow: { key: "decision-flow", displayValue: null },
  idSection: { key: "id-section", displayValue: null },
} as const;

const BLOCK_FIELD_MAPPING = {
  case_details: "case_id",
  table: "source",
  markdown: "source",
  entity_details: "entity",
  event_details: "event",
  related_cases: "entity",
  related_entities: "entity",
  related_events: "entity",
} as const;

export type BlockFieldName =
  (typeof BLOCK_FIELD_MAPPING)[keyof typeof BLOCK_FIELD_MAPPING];

const getBlockConfigFieldName = (
  blockKind: Config1["kind"] | undefined,
): BlockFieldName => {
  return blockKind ? BLOCK_FIELD_MAPPING[blockKind] : "case_id";
};

export const createBlockConfigPayload = (
  blockKind: Config1["kind"],
  value: LinkedBlockConfigAttribute | null,
): Config1 => {
  const baseConfig = { kind: blockKind };

  if (!value) {
    return baseConfig as Config1;
  }

  const attributeValue =
    value.kind === LinkedBlockConfigAttributeKind.ADDITIONAL_INPUTS &&
    value.key === null
      ? { kind: value.kind }
      : value;

  switch (blockKind) {
    case "case_details":
      return { ...baseConfig, case_id: attributeValue } as Config1;

    case "table":
    case "markdown":
      return { ...baseConfig, source: attributeValue } as Config1;

    case "entity_details":
    case "related_cases":
    case "related_entities":
    case "related_events":
      return { ...baseConfig, entity: attributeValue } as Config1;

    case "event_details":
      return { ...baseConfig, event: attributeValue } as Config1;

    default:
      return baseConfig as Config1;
  }
};

const getDecisionFlowOption = (): Option => ({
  key: DECISION_FLOW_MAPPING_KEY,
  value: "Map this field in Decision Flow",
  sectionKey: "decision-flow",
});

type BlockKindConfig = {
  defaultValue?: DefaultValue;
  showInput?: boolean;
  buildOptions: (
    templateFormValues: CaseTemplateFormValues,
    getPropertyIcon?: PropertyIconGetter,
  ) => {
    options: Option[];
    sections: Section[];
    defaultValue?: DefaultValue;
  };
};

type PropertyType = "string" | "entity_ref" | "event_ref";
type PropertyIconGetter = (property: CasePropertiesValue) => IconDefinition;
type DefaultValue = {
  kind: LinkedBlockConfigAttributeKind;
  key: string | null;
};

const getPropertiesOptionsByType = (
  templateFormValues: CaseTemplateFormValues,
  propertyType: PropertyType,
  sectionKey: string,
  getPropertyIcon?: PropertyIconGetter,
): Option[] => {
  const caseProperties = templateFormValues.attributes?.case_properties || [];

  return caseProperties
    .filter((property) => property.type === propertyType && property.key)
    .map((property) => ({
      key: `${property.key}#properties`,
      value: property.display_name || property.key,
      icon:
        propertyType !== "string" && getPropertyIcon
          ? getPropertyIcon(property)
          : TYPE_ICONS.string,
      sectionKey,
    }));
};

const getDefaultValueFromOptions = (
  options: Option[],
  kind: LinkedBlockConfigAttributeKind = LinkedBlockConfigAttributeKind.PROPERTIES,
): DefaultValue | undefined =>
  options.length > 0 ? { kind, key: options[0].key.split("#")[0] } : undefined;

const buildReferenceBlockConfig = (
  templateFormValues: CaseTemplateFormValues,
  propertyType: PropertyType,
  getPropertyIcon?: PropertyIconGetter,
) => {
  const sections = [SECTIONS.properties];
  const options = getPropertiesOptionsByType(
    templateFormValues,
    propertyType,
    "properties",
    getPropertyIcon,
  );

  return {
    options,
    sections,
    defaultValue: getDefaultValueFromOptions(options),
  };
};

export const BLOCK_KIND_CONFIGS: Record<Config1["kind"], BlockKindConfig> = {
  case_details: {
    defaultValue: {
      kind: LinkedBlockConfigAttributeKind.ATTRIBUTES,
      key: "id",
    },
    buildOptions: (templateFormValues) => {
      const sections: Section[] = [
        SECTIONS.idSection,
        SECTIONS.properties,
        SECTIONS.decisionFlow,
      ];

      const options: Option[] = [
        {
          key: "id#attributes",
          value: "Case ID",
          icon: TYPE_ICONS.string,
          sectionKey: "id-section",
        },
        ...getPropertiesOptionsByType(
          templateFormValues,
          "string",
          "properties",
        ),
      ];

      return { options, sections };
    },
  },

  table: {
    showInput: false, // Don't show input dropdown for table blocks
    defaultValue: {
      kind: LinkedBlockConfigAttributeKind.ADDITIONAL_INPUTS,
      key: null,
    },
    buildOptions: () => {
      // For table blocks, the decision flow option is implicitly selected
      return { options: [], sections: [] };
    },
  },

  markdown: {
    defaultValue: {
      kind: LinkedBlockConfigAttributeKind.ADDITIONAL_INPUTS,
      key: null,
    },
    buildOptions: (templateFormValues) => {
      const sections = [SECTIONS.properties, SECTIONS.decisionFlow];
      const options = [
        ...getPropertiesOptionsByType(
          templateFormValues,
          "string",
          "properties",
        ),
        getDecisionFlowOption(),
      ];

      return { options, sections };
    },
  },

  entity_details: {
    buildOptions: (templateFormValues, getPropertyIcon) => {
      return buildReferenceBlockConfig(
        templateFormValues,
        "entity_ref",
        getPropertyIcon,
      );
    },
  },

  event_details: {
    buildOptions: (templateFormValues, getPropertyIcon) => {
      return buildReferenceBlockConfig(
        templateFormValues,
        "event_ref",
        getPropertyIcon,
      );
    },
  },

  related_cases: {
    buildOptions: (templateFormValues, getPropertyIcon) => {
      return buildReferenceBlockConfig(
        templateFormValues,
        "entity_ref",
        getPropertyIcon,
      );
    },
  },

  related_entities: {
    buildOptions: (templateFormValues, getPropertyIcon) => {
      return buildReferenceBlockConfig(
        templateFormValues,
        "entity_ref",
        getPropertyIcon,
      );
    },
  },

  related_events: {
    buildOptions: (templateFormValues, getPropertyIcon) => {
      return buildReferenceBlockConfig(
        templateFormValues,
        "entity_ref",
        getPropertyIcon,
      );
    },
  },
};

export const getLinkedAttributeKind = (
  optionKey: string,
  templateFormValues?: CaseTemplateFormValues,
): LinkedBlockConfigAttributeKind => {
  if (optionKey === DECISION_FLOW_MAPPING_KEY) {
    return LinkedBlockConfigAttributeKind.ADDITIONAL_INPUTS;
  }

  const isUserDefinedProperty =
    templateFormValues?.attributes?.case_properties?.some(
      (prop) => prop.key === optionKey,
    ) ?? false;

  return isUserDefinedProperty
    ? LinkedBlockConfigAttributeKind.PROPERTIES
    : LinkedBlockConfigAttributeKind.ATTRIBUTES;
};

type UseBlockInputOptionsResult = {
  options: Option[];
  sections: Section[];
  blockKind: Config1["kind"] | undefined;
  shouldShowInput: boolean;
  configFieldName: BlockFieldName;
};

export const useBlockInputOptions = (
  blockIndex: number,
): UseBlockInputOptionsResult => {
  const { control } = useFormContext<CaseTemplateFormValues>();
  const getPropertyIcon = useGetCasePropertyIcon();

  const blockConfigPath =
    `attributes.case_blocks_configs.${blockIndex}` as const;
  const currentBlockKind = useWatch({
    control,
    name: `${blockConfigPath}.config.kind`,
  }) as Config1["kind"] | undefined;

  const caseProperties = useWatch({
    control,
    name: "attributes.case_properties",
  });

  const inputOptionsData = useMemo(() => {
    if (!currentBlockKind || !caseProperties) {
      return {
        options: [],
        sections: [],
      };
    }

    const config = BLOCK_KIND_CONFIGS[currentBlockKind];
    if (!config) {
      return {
        options: [],
        sections: [],
      };
    }

    const templateFormValues = {
      attributes: { case_properties: caseProperties },
    } as CaseTemplateFormValues;

    const { options, sections } = config.buildOptions(
      templateFormValues,
      getPropertyIcon,
    );

    return {
      options,
      sections,
    };
  }, [currentBlockKind, caseProperties, getPropertyIcon]);

  const config = currentBlockKind ? BLOCK_KIND_CONFIGS[currentBlockKind] : null;
  const shouldShowInput = config?.showInput !== false;
  const configFieldName = getBlockConfigFieldName(currentBlockKind);

  return {
    ...inputOptionsData,
    blockKind: currentBlockKind,
    shouldShowInput,
    configFieldName,
  };
};
